/**
 * Shared web primitives (Signal Desk). Styling lives in app.css / transcript-ui styles and reads
 * only @viko/tokens variables, so light/dark and density come for free.
 *
 * Usage:
 *   <Button variant="primary|secondary|ghost|danger" size="sm|md" icon={<Plus size={16} />}>
 *     Add project
 *   </Button>
 *   <IconButton label="Close" icon={<X size={16} />} onClick={close} />
 *   <Field label="Name" hint="Shown in the sidebar"><Input value={v} onChange={…} /></Field>
 *   <Dialog open={open} onClose={close} title="Add project"
 *     footer={<><Button onClick={close}>Cancel</Button><Button variant="primary">Add</Button></>}>
 *     …form fields…
 *   </Dialog>
 * Dialog is centred at every width (nothing opens from the bottom of the page); Sheet
 * (re-exported from transcript-ui) is a side panel on desktop. Both trap Esc, move focus in and
 * restore it.
 */
import { Button, formatAgo, Sheet } from '@viko/transcript-ui'
import { AlertCircle, CheckCircle2, Paperclip } from 'lucide-react'
import {
  type ButtonHTMLAttributes,
  createContext,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
} from 'react'
import {
  ENCRYPTED_UNAVAILABLE_TEXT,
  EncryptedUnavailable,
  isEncryptedUnavailable,
} from '../lib/content-errors.js'

export { Button, Sheet }

export function PageHead({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <header className="page-head">
      <h1>{title}</h1>
      <span className="flex-1" />
      {children}
    </header>
  )
}

export function SectionTitle({
  children,
  action,
  decision,
}: {
  children: ReactNode
  action?: ReactNode
  /** Marks the section holding the decision that unblocks work (brand stream signal). */
  decision?: boolean
}) {
  return (
    <h2 className={`section-title${decision ? ' section-title--decision' : ''}`}>
      {children}
      <span className="flex-1" />
      {action}
    </h2>
  )
}

export function Empty({
  title,
  children,
  center,
  icon,
  action,
}: {
  title: string
  children?: ReactNode
  center?: boolean
  icon?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className={`empty${center ? ' empty--center' : ''}`}>
      {icon ? (
        <span className="empty__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <div className="text-[16px] font-semibold text-text-1">{title}</div>
      {children ? <div className="max-w-[72ch]">{children}</div> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  )
}

// ---- controls ----------------------------------------------------------------------------------

/** Icon-only button; `label` is required and becomes the accessible name and tooltip. */
export function IconButton({
  label,
  icon,
  size = 'md',
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string
  icon: ReactNode
  size?: 'md' | 'lg'
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`vk-iconbtn${size === 'lg' ? ' vk-iconbtn--lg' : ''}${className ? ` ${className}` : ''}`}
      {...rest}
    >
      {icon}
    </button>
  )
}

/** Label + control + optional hint/error. Wrap Input/Textarea/Select/FileInput. */
export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children
    <label className={`field${className ? ` ${className}` : ''}`}>
      <span>{label}</span>
      {children}
      {error ? (
        <span className="field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="field__hint">{hint}</span>
      ) : null}
    </label>
  )
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`input${className ? ` ${className}` : ''}`} {...rest} />
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`input${className ? ` ${className}` : ''}`} {...rest} />
}

export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`input${className ? ` ${className}` : ''}`} {...rest} />
}

/** File picker styled as a drop-zone row; the native input stays keyboard-reachable. */
export function FileInput({
  label = 'Choose file',
  fileName,
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: string
  /** Currently chosen file name(s), shown instead of the prompt. */
  fileName?: string
}) {
  return (
    <label className={`file-input${className ? ` ${className}` : ''}`}>
      <Paperclip size={16} aria-hidden="true" className="flex-none text-text-3" />
      <span className="file-input__name">{fileName || 'No file chosen'}</span>
      <span className="vk-btn vk-btn--secondary vk-btn--sm">{label}</span>
      <input type="file" {...rest} />
    </label>
  )
}

/** Small status/meta label. Tones map to status colours; never colour-only (keep the text). */
export function Badge({
  tone,
  children,
  className,
}: {
  tone?: 'run' | 'wait' | 'ok' | 'fail' | 'accent'
  children: ReactNode
  className?: string
}) {
  return (
    <span className={`badge${tone ? ` badge--${tone}` : ''}${className ? ` ${className}` : ''}`}>
      {children}
    </span>
  )
}

/** Hairline surface. `raised` adds the first elevation step. */
export function Card({
  children,
  raised,
  className,
  ...rest
}: { children: ReactNode; raised?: boolean; className?: string } & Omit<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'children'
>) {
  return (
    <div
      className={`card${raised ? ' card--raised' : ''}${className ? ` ${className}` : ''}`}
      {...rest}
    >
      {children}
    </div>
  )
}

/** Accessible tab strip (role=tablist, arrow keys move between tabs). */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  className,
}: {
  tabs: { id: T; label: ReactNode; count?: number }[]
  value: T
  onChange: (id: T) => void
  label: string
  className?: string
}) {
  const id = useId()
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    const i = tabs.findIndex((t) => t.id === value)
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]
    if (!next) return
    onChange(next.id)
    document.getElementById(`${id}-${next.id}`)?.focus()
  }
  return (
    <div
      className={`tabs${className ? ` ${className}` : ''}`}
      role="tablist"
      aria-label={label}
      onKeyDown={onKey}
    >
      {tabs.map((t) => (
        <button
          key={t.id}
          id={`${id}-${t.id}`}
          type="button"
          role="tab"
          aria-selected={t.id === value}
          tabIndex={t.id === value ? 0 : -1}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.count != null ? <span className="tabs__count">{t.count}</span> : null}
        </button>
      ))}
    </div>
  )
}

/**
 * Modal dialog: a centred panel at every width (never a bottom sheet). Built on transcript-ui's
 * Sheet (focus in/out, Esc, scrim click). `footer` renders a sticky action bar — put the
 * primary action last.
 */
export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: 'md' | 'lg'
}) {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      placement="center"
      size={size}
    >
      {children}
      {footer ? <div className="dialog-foot">{footer}</div> : null}
    </Sheet>
  )
}

export function SkeletonRows({ n = 4 }: { n?: number }) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: n }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
        <div key={i} className="row" aria-hidden="true">
          <div className="skeleton h-8 w-8 rounded-full" />
          <div className="grid flex-1 gap-2">
            <div className="skeleton h-4 w-2/3" />
            <div className="skeleton h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function Meter({ pct, label }: { pct: number; label: string }) {
  const p = Math.max(0, Math.min(100, pct))
  return (
    <div
      className={`meter${p >= 90 ? ' is-fail' : p >= 75 ? ' is-warn' : ''}`}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(p)}
      aria-label={label}
    >
      <i style={{ width: `${p}%` }} />
    </div>
  )
}

export function Ago({ ts }: { ts: number | undefined }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])
  if (!ts) return null
  return <time dateTime={new Date(ts).toISOString()}>{formatAgo(ts, now)}</time>
}

// ---- toasts ------------------------------------------------------------------------------------

interface ToastAction {
  label: string
  /** A single key that runs it while the toast is up (not while typing), shown as a keycap. */
  key?: string
  run: () => void
}

export interface ToastOptions {
  /** Optimistic + undo instead of a confirm: the action rides on the toast until it closes. */
  action?: ToastAction
  /** How long it stays up (default 2.6 s, errors 6 s). */
  ms?: number
}

interface Toast {
  id: number
  text: string
  tone: 'ok' | 'error'
  action?: ToastAction
}

type ToastFn = (text: string, tone?: Toast['tone'], opts?: ToastOptions) => void

/** Design language §8: toasts auto-dismiss after 2.6 s (errors stay 6 s). */
export const TOAST_MS = 2600

const ToastCtx = createContext<ToastFn>(() => {})

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const drop = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), [])
  const push = useCallback<ToastFn>(
    (text, tone = 'ok', opts) => {
      const id = Date.now() + Math.random()
      setToasts((t) => [
        ...t.slice(-2),
        { id, text, tone, ...(opts?.action ? { action: opts.action } : {}) },
      ])
      setTimeout(() => drop(id), opts?.ms ?? (tone === 'error' ? 6000 : TOAST_MS))
    },
    [drop],
  )
  const fire = useCallback(
    (t: Toast) => {
      drop(t.id)
      t.action?.run()
    },
    [drop],
  )
  // A toast's action key (Undo: `u`) works from anywhere while it is up, never while typing.
  const keyed = toasts.filter((t) => t.action?.key).at(-1)
  useEffect(() => {
    if (!keyed) return
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== keyed.action?.key || e.metaKey || e.ctrlKey || e.altKey) return
      const el = e.target as HTMLElement | null
      if (el?.closest?.('input, textarea, select, [contenteditable="true"], [contenteditable=""]'))
        return
      e.preventDefault()
      fire(keyed)
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  }, [keyed, fire])
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast${t.tone === 'error' ? ' is-error' : ''}`}>
            {t.tone === 'error' ? (
              <AlertCircle size={16} className="text-status-fail" aria-hidden="true" />
            ) : (
              <CheckCircle2 size={16} className="text-status-ok" aria-hidden="true" />
            )}
            <span className="toast__t">{t.text}</span>
            {t.action ? (
              <button type="button" className="toast__act" onClick={() => fire(t)}>
                <span>{t.action.label}</span>
                {t.action.key ? <kbd>{t.action.key}</kbd> : null}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

export const useToast = () => useContext(ToastCtx)

export function errorText(e: unknown): string {
  // A refused plaintext lane is a known product state, never a raw hub code.
  if (isEncryptedUnavailable(e) && !(e instanceof EncryptedUnavailable))
    return ENCRYPTED_UNAVAILABLE_TEXT
  return (e as Error)?.message || 'Something went wrong.'
}
